import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Text,
  Content,
  Col,
  Row,
  RadioGroup,
  Radio,
  Grid,
  FlexboxGrid,
  Button,
} from 'rsuite';
import {
  removeRegistrantData,
  updatePermitApplicationData,
} from '../../store/slices/apply/applySlice';
import FlowPageLayout from '../../components/layout/FlowPageLayout';
import PortalHeader from '../../components/layout/PortalHeader';
import SchemaTyped from 'rsuite/esm/Schema/Schema';
import {
  companyNameValidation,
  emailRequiredValidation,
  nameRequiredValidation,
  phoneRequiredValidation,
  requiredValidation,
} from '../../common/validation';
import {
  selectApplyApplicationData,
  selectConfigExpressHasMultipleOwners,
  selectConfigExpressRegistrantOptions,
} from '../../store/slices/config/configSelectors';
import PersonDetails from '../../components/form/PersonDetails';
import { selectCurrentUser } from '../../store/slices/auth/authSelectors';
import { FLOWS_ENUM } from '../../common/enums';
import { REGISTRANT_OPTIONS } from '../../components/payment/constants';

const ApplyRegistrant = () => {
  // State and dependencies
  const dispatch = useDispatch();
  const form = useRef();
  const [selectedRegistrantOption, setSelectedRegistrantOption] = useState(
    REGISTRANT_OPTIONS.OWNER
  );
  const [formValues, setFormValues] = useState({});
  const [formError, setFormError] = useState({});

  const [owners, setOwners] = useState([]);

  const ownerFormRefs = useRef({});

  // Selectors
  const selectedIsMultipleOwnersEnabled = useSelector(
    selectConfigExpressHasMultipleOwners
  );
  const selectedRegistrantOptions = useSelector(
    selectConfigExpressRegistrantOptions
  );
  const selectedLoggedInUserDetails = useSelector(selectCurrentUser);
  const selectedApplicationData = useSelector(selectApplyApplicationData);

  const { registrant } = useSelector(selectApplyApplicationData);
  const [defaultRadioOption, setDefaultRadioOption] = useState(
    registrant?.registrantType ?? REGISTRANT_OPTIONS.OWNER
  );
  const [hasData, setHasData] = useState(registrant !== undefined);

  // Form setup
  const formModel = useMemo(() => {
    return {
      firstName: '',
      lastName: '',
      companyName: '',
      email: '',
      phone: '',
      mailingAddress: '',
      sameAsPropertyAddress: false,
    };
  }, []);

  const { BooleanType } = SchemaTyped.Types;

  const validationModel = {
    firstName: nameRequiredValidation,
    lastName: nameRequiredValidation,
    email: emailRequiredValidation,
    companyName: companyNameValidation,
    phone: phoneRequiredValidation,
    mailingAddress: requiredValidation,
    sameAsPropertyAddress: BooleanType(),
  };

  useEffect(() => {
    if (hasData) {
      setSelectedRegistrantOption(registrant?.registrantType);
      setDefaultRadioOption(registrant?.registrantType);
      if (registrant?.owner.length > 0) {
        setFormValues({
          firstName: registrant?.owner[0].firstName ?? '',
          lastName: registrant?.owner[0].lastName ?? '',
          companyName: registrant?.owner[0].companyName ?? '',
          email: registrant?.owner[0].email ?? '',
          phone: registrant?.owner[0].phone ?? '',
          mailingAddress: registrant?.owner[0].mailingAddress ?? '',
          sameAsPropertyAddress:
            registrant?.owner[0].mailingAddress &&
            registrant?.owner[0].mailingAddress.toLowerCase() ===
              selectedApplicationData?.property?.propertyaddress.toLowerCase(),
        });
        if (registrant?.owner.length > 1) {
          loadExistingAdditionalOwners(registrant?.owner.slice(1));
        }
      }
    }
  }, [hasData, registrant, defaultRadioOption]);

  const hideOrShowAddressEntry = (v) => {
    dispatch(removeRegistrantData());
    setFormValues({});
    setHasData(false);
    if (
      form?.current &&
      selectedRegistrantOption === REGISTRANT_OPTIONS.OWNER
    ) {
      // When you change to a different form component, clear the errors but keep the data
      setFormError({});
    }
    setSelectedRegistrantOption(v);
  };

  const validateOwnerForms = () => {
    let isValid = true;
    Object.keys(ownerFormRefs.current).forEach((k) => {
      if (!ownerFormRefs.current[k].current.check()) {
        isValid = false;
      }
    });
    return isValid;
  };

  const dispatchRegistrant = () => {
    const loggedInPerson = {
      ...selectedLoggedInUserDetails,
    };

    let additionalOwners = [];
    if (Object.keys(ownerFormRefs.current).length > 0) {
      if (!validateOwnerForms()) return;
      additionalOwners = owners.map((o) => o.formValues);
    }

    if (isRegistrantOwner()) {
      // If the logged in user is the owner:
      // - Set the logged in user to be the "owner" in redux
      // - set them as the registrant
      // - set the registrantType as "owner"

      // Get logged in user address
      let ownerDetails = { ...loggedInPerson };

      // The owner is also the registrant
      let registrantDetails = { ...ownerDetails };

      dispatch(
        updatePermitApplicationData({
          registrant: {
            registrantType: selectedRegistrantOption,
            owner: [{ ...ownerDetails }, ...additionalOwners],
            registrant: { ...registrantDetails },
          },
        })
      );
    } else {
      // If the logged in user is a property manager:
      // - Get the owner details from the form (if valid)
      // - Set the owner details as the "owner" in redux,
      // - Set the logged in user as the registrant
      // - Set the logged in user as the property manager
      // - Set the registrantType as "property_manager"

      // Check the form and dispatch form values
      if (!form?.current?.check()) return;

      const formPerson = {
        firstName: formValues?.firstName,
        lastName: formValues?.lastName,
        email: formValues?.email,
        phone: formValues?.phone,
        mailingAddress: formValues?.mailingAddress,
        companyName: formValues?.companyName,
      };

      let ownerDetails = { ...formPerson };
      let registrantDetails = { ...loggedInPerson };

      dispatch(
        updatePermitApplicationData({
          registrant: {
            registrantType: selectedRegistrantOption,
            owner: [
              { ...ownerDetails },
              ...additionalOwners.map((o) => {
                // Get rid of the sameAsPropertyAddress property
                let newOwner = { ...o };
                delete newOwner.sameAsPropertyAddress;
                return newOwner;
              }),
            ],
            registrant: { ...registrantDetails },
            [selectedRegistrantOption]: { ...registrantDetails },
          },
        })
      );
    }
    return true;
  };

  const dispatchClearRegistrant = () => {
    dispatch(removeRegistrantData());
    return true;
  };

  const isRegistrantOwner = () => {
    return selectedRegistrantOption === 'owner';
  };

  const createNewOwner = () => ({
    key: `owner_${owners.length}`,
    formValues: {},
    formErrors: {},
    model: formModel,
    validationModel: validationModel,
    preloadedData: false,
  });

  const mapExistingAdditionalOwners = (existingOwner, keyNum) => {
    return {
      key: `owner_${keyNum}`,
      formValues: {
        ...existingOwner,
        sameAsPropertyAddress:
          existingOwner.mailingAddress &&
          existingOwner.mailingAddress.toLowerCase() ===
            selectedApplicationData?.property?.propertyaddress.toLowerCase(),
      },
      formErrors: {},
      model: formModel,
      validationModel: validationModel,
      preloadedData: true,
    };
  };

  const onAddAdditionalOwner = () => {
    const newOwner = createNewOwner();
    ownerFormRefs.current[newOwner.key] = createRef();
    setOwners((prev) => [...prev, newOwner]);
  };

  const loadExistingAdditionalOwners = (existingOwners) => {
    let tempOwners = [...owners];
    for (let existingOwner of existingOwners) {
      const additionalOwner = mapExistingAdditionalOwners(
        existingOwner,
        tempOwners.length
      );
      tempOwners.push(additionalOwner);
    }
    for (let owner of tempOwners) {
      ownerFormRefs.current[owner.key] = createRef();
    }
    setOwners(tempOwners);
  };

  const handleFormValuesChange = (key, newValues) => {
    setOwners((prev) =>
      prev.map((owner) =>
        owner.key === key ? { ...owner, formValues: newValues } : owner
      )
    );
  };

  const handleFormErrorsChange = (key, newErrors) => {
    setOwners((prev) =>
      prev.map((owner) =>
        owner.key === key ? { ...owner, formErrors: newErrors } : owner
      )
    );
  };

  const handleAdditionalOwnerSameAsPropertyAddressChanged = (key, checked) => {
    setOwners((prev) =>
      prev.map((owner) =>
        owner.key === key
          ? {
              ...owner,
              formValues: {
                ...owner.formValues,
                sameAsPropertyAddress: checked,
                mailingAddress: checked
                  ? selectedApplicationData?.property?.propertyaddress
                  : owner.formValues.mailingAddress,
              },
            }
          : owner
      )
    );
  };

  const onRemoveOwner = (key) => {
    setOwners((prev) => prev.filter((owner) => owner.key !== key));
    delete ownerFormRefs.current[key];
  };

  const onSameAsPropertyAddressChanged = (checked) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      sameAsPropertyAddress: checked,
      mailingAddress: checked
        ? selectedApplicationData?.property?.propertyaddress
        : prevFormValues.mailingAddress,
    }));
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader flow={FLOWS_ENUM.APPLY_LICENSE} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={24}>
        <Content>
          <FlowPageLayout
            title="Registrant"
            onNext={dispatchRegistrant}
            onBack={dispatchClearRegistrant}
          >
            <Grid
              className="mt-12 ml-0 "
              xs={24}
              lg={12}
              fluid
            >
              <Row>
                <Col xs={24}>
                  <Text>Are you the owner of the rental property?</Text>
                  <RadioGroup
                    name="radio-option-group"
                    defaultValue={defaultRadioOption}
                    onChange={hideOrShowAddressEntry}
                  >
                    {selectedRegistrantOptions?.property_owner && (
                      <Radio value={REGISTRANT_OPTIONS.OWNER}>
                        Yes, I&apos;m the owner
                      </Radio>
                    )}
                    {selectedRegistrantOptions?.property_manager && (
                      <Radio value={REGISTRANT_OPTIONS.PROPERTY_MANAGER}>
                        No, I&apos;m a property manager
                      </Radio>
                    )}
                    {selectedRegistrantOptions?.tenant && (
                      <Radio value={REGISTRANT_OPTIONS.TENANT}>
                        No, I&apos;m a tenant
                      </Radio>
                    )}
                  </RadioGroup>
                </Col>
              </Row>
              {!isRegistrantOwner() ? (
                <>
                  <Row className="mt-24 mb-8">
                    <Col xs={12}>
                      <Text className="text-bold">Owner Details</Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      xs={24}
                      md={12}
                    >
                      <PersonDetails
                        ref={form}
                        setFormError={setFormError}
                        setFormValues={(v) => {
                          setFormValues(v);
                        }}
                        formModel={formModel}
                        formError={formError}
                        formValues={formValues}
                        validationModel={validationModel}
                        showSameAsPropertyAddressCheckboxOption={true}
                        onSameAsPropertyAddressChanged={
                          onSameAsPropertyAddressChanged
                        }
                      />
                    </Col>
                  </Row>
                </>
              ) : null}
              {owners.map((o, index) => {
                return (
                  <>
                    <Row className="mt-8">
                      <Col
                        xs={24}
                        md={12}
                      >
                        <FlexboxGrid justify="space-between">
                          <Text className="text-bold">
                            Owner {index + 2} Details
                          </Text>
                          <a
                            href="#"
                            onClick={() => onRemoveOwner(o.key)}
                          >
                            Remove owner
                          </a>
                        </FlexboxGrid>
                      </Col>
                    </Row>
                    <Row>
                      <Col
                        xs={24}
                        md={12}
                      >
                        <PersonDetails
                          ref={ownerFormRefs.current[o.key]}
                          formModel={o.model}
                          validationModel={o.validationModel}
                          formValues={o.formValues}
                          formError={o.formErrors}
                          setFormValues={(newValues) =>
                            handleFormValuesChange(o.key, newValues)
                          }
                          setFormError={(newErrors) =>
                            handleFormErrorsChange(o.key, newErrors)
                          }
                          showSameAsPropertyAddressCheckboxOption={true}
                          onSameAsPropertyAddressChanged={(checked) =>
                            handleAdditionalOwnerSameAsPropertyAddressChanged(
                              o.key,
                              checked
                            )
                          }
                        />
                      </Col>
                    </Row>
                  </>
                );
              })}
              {selectedIsMultipleOwnersEnabled ? (
                <Row>
                  <Col>
                    <Button
                      className="btn-tertiary mt-16 mb-8"
                      onClick={onAddAdditionalOwner}
                    >
                      Add additional owner
                    </Button>
                  </Col>
                </Row>
              ) : null}
            </Grid>
          </FlowPageLayout>
        </Content>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ApplyRegistrant;
